import {
    useInfiniteQuery,
    type DefaultError,
    type InfiniteData,
    type QueryKey,
    type UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { useMemo } from 'react';

/** Every paged list (`studios.list`, `games.list`) reads 50 rows at a time. */
export const PAGE_SIZE = 50;

/** Every cursor-paged procedure's paging: the server's `nextCursor`, from none. */
export const CURSOR_PAGING = {
    getNextPageParam: (last: { nextCursor: string | null }) => last.nextCursor ?? undefined,
    initialCursor: undefined,
};

type Page = { columns: readonly unknown[]; rows: readonly unknown[] };

type PagedRows<TPage extends Page> = {
    columns: TPage['columns'];
    rows: TPage['rows'];
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => void;
    hasMore: boolean;
    loadMore: () => void;
    isLoadingMore: boolean;
};

/**
 * One infinite tRPC list, flattened into the shape both `hooks/useStudioRows` and
 * `hooks/useGameRows` want: the first page's columns, every page's rows concatenated, and
 * load-more state that reports its own failure (`isError`) rather than folding it into an empty
 * list.
 *
 * `rows` and `columns` are memoised on `query.data.pages` — react-query keeps that reference
 * stable across a render its own cache didn't change, so a keystroke elsewhere on the page (the
 * search box, a selection toggle) no longer rebuilds the `flatMap` and every row a memoised
 * `MatrixRow` would otherwise see as "new".
 */
export const usePagedRows = <
    TPage extends Page,
    TError = DefaultError,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = unknown,
>(
    options: UseInfiniteQueryOptions<TPage, TError, InfiniteData<TPage, TPageParam>, TQueryKey, TPageParam>,
): PagedRows<TPage> => {
    const query = useInfiniteQuery(options);
    const pages = query.data?.pages;

    const columns = useMemo(() => (pages?.[0]?.columns ?? []) as TPage['columns'], [pages]);
    const rows = useMemo(() => (pages ?? []).flatMap((page) => page.rows) as TPage['rows'], [pages]);

    return {
        columns,
        rows,
        isLoading: query.isLoading,
        isSuccess: query.isSuccess,
        isError: query.isError,
        error: query.error,
        refetch: () => void query.refetch(),
        hasMore: Boolean(query.hasNextPage),
        loadMore: () => void query.fetchNextPage(),
        isLoadingMore: query.isFetchingNextPage,
    };
};
